/**
 * Transactions slice — in-memory cache + indices for fast lookup.
 *
 * Exposes:
 * - `items`: full list (matches Sheets row order)
 * - `byId`: O(1) id → row lookup
 * - `byDate` / `byCategory` / `byBook`: id arrays grouped by key
 *
 * Indices are rebuilt wholesale on `setItems`, and incrementally updated by
 * `upsert` / `remove` so callers don't pay an O(n) rebuild on every mutation.
 *
 * The slice creator follows the same standalone-creator pattern as
 * `auth.ts` / `sync.ts` — it does not import the global store, so the wave
 * 13 store wiring can compose it without an import cycle.
 */

import type { TransactionRow } from '@/lib/sheets/types';

export interface TransactionsState {
  items: TransactionRow[];
  byId: Record<string, TransactionRow>;
  byDate: Record<string, string[]>;
  byCategory: Record<string, string[]>;
  byBook: Record<string, string[]>;
}

export interface TransactionsActions {
  setItems: (items: TransactionRow[]) => void;
  upsert: (item: TransactionRow) => void;
  remove: (id: string) => void;
}

export type TransactionsSlice = TransactionsState & TransactionsActions;

export const initialTransactionsState: TransactionsState = {
  items: [],
  byId: {},
  byDate: {},
  byCategory: {},
  byBook: {},
};

interface Indices {
  byId: Record<string, TransactionRow>;
  byDate: Record<string, string[]>;
  byCategory: Record<string, string[]>;
  byBook: Record<string, string[]>;
}

function buildIndices(items: TransactionRow[]): Indices {
  const byId: Record<string, TransactionRow> = {};
  const byDate: Record<string, string[]> = {};
  const byCategory: Record<string, string[]> = {};
  const byBook: Record<string, string[]> = {};

  for (const item of items) {
    byId[item.id] = item;
    pushIndex(byDate, item.date, item.id);
    pushIndex(byCategory, item.category_id, item.id);
    pushIndex(byBook, item.book_id, item.id);
  }

  return { byId, byDate, byCategory, byBook };
}

function pushIndex(idx: Record<string, string[]>, key: string, id: string): void {
  if (!key) return;
  const list = idx[key];
  if (list) {
    if (!list.includes(id)) list.push(id);
  } else {
    idx[key] = [id];
  }
}

function removeFromIndex(idx: Record<string, string[]>, key: string, id: string): void {
  if (!key) return;
  const list = idx[key];
  if (!list) return;
  const next = list.filter((x) => x !== id);
  if (next.length === 0) delete idx[key];
  else idx[key] = next;
}

export const createTransactionsSlice =
  <T extends TransactionsSlice>() =>
  (
    set: (
      partial: Partial<T> | ((state: T) => Partial<T>),
      replace?: boolean,
    ) => void,
  ): TransactionsSlice => ({
    ...initialTransactionsState,

    setItems: (items) => {
      const indices = buildIndices(items);
      set({
        items,
        byId: indices.byId,
        byDate: indices.byDate,
        byCategory: indices.byCategory,
        byBook: indices.byBook,
      } as Partial<T>);
    },

    upsert: (item) =>
      set((state) => {
        const existing = state.byId[item.id];
        const items = existing
          ? state.items.map((x) => (x.id === item.id ? item : x))
          : [...state.items, item];

        // clone indices to avoid mutating prior state
        const byDate = cloneIdx(state.byDate);
        const byCategory = cloneIdx(state.byCategory);
        const byBook = cloneIdx(state.byBook);

        if (existing) {
          if (existing.date !== item.date) {
            removeFromIndex(byDate, existing.date, item.id);
          }
          if (existing.category_id !== item.category_id) {
            removeFromIndex(byCategory, existing.category_id, item.id);
          }
          if (existing.book_id !== item.book_id) {
            removeFromIndex(byBook, existing.book_id, item.id);
          }
        }

        pushIndex(byDate, item.date, item.id);
        pushIndex(byCategory, item.category_id, item.id);
        pushIndex(byBook, item.book_id, item.id);

        return {
          items,
          byId: { ...state.byId, [item.id]: item },
          byDate,
          byCategory,
          byBook,
        } as Partial<T>;
      }),

    remove: (id) =>
      set((state) => {
        const existing = state.byId[id];
        if (!existing) return {} as Partial<T>;

        const byId = { ...state.byId };
        delete byId[id];

        const byDate = cloneIdx(state.byDate);
        const byCategory = cloneIdx(state.byCategory);
        const byBook = cloneIdx(state.byBook);
        removeFromIndex(byDate, existing.date, id);
        removeFromIndex(byCategory, existing.category_id, id);
        removeFromIndex(byBook, existing.book_id, id);

        return {
          items: state.items.filter((x) => x.id !== id),
          byId,
          byDate,
          byCategory,
          byBook,
        } as Partial<T>;
      }),
  });

function cloneIdx(idx: Record<string, string[]>): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const k of Object.keys(idx)) {
    const v = idx[k];
    if (v) out[k] = [...v];
  }
  return out;
}
